import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout';
import { TicketDeskFooter as CatalogDeskFooter } from '../../../shared/ticket/TicketDeskFooter';
import { POS_DESK_CATALOG_GUTTER } from '../../application/posLayout.constants';
import { MobilePagination as StockMobilePagination } from '../../../shared/ui/MobilePagination';
import { usePosCatalogPage } from '../../application/usePosCatalogPage';
import { usePosCatalogLayout } from './usePosCatalogLayout';
import { usePosCatalogFilters } from './usePosCatalogFilters';
import { usePosQuickAccessRail } from '../../application/usePosQuickAccessRail';
import { hasPosCatalogPanelFilters } from '../../application/posActiveCatalogFiltersSummary';
import { parseRailSelection, posDeskCatalogListHeaderActive } from '../../application/posQuickAccessRail';
import type { ProductSearchRow } from '../../../catalog/domain-api';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService';
import { planAllowsPromotions } from '../../../shell/feature-flags/planEntitlementsService';
import { setPosFulfillmentType } from '../../../shared/pos/posHandoffSettings';
const VIEWPORT_HEIGHT_EPSILON_PX = 24;

type Params = {
  currentBranchId: string | null | undefined;
  windowWidth: number;
  selectedCategory: string | 'all' | null;
  setCategory: (category: string | 'all' | null) => void;
  categories: import('../../../catalog/public-api').CategoryRecord[];
  canEdit: boolean;
  canViewPos: boolean;
  canOpenCashManagementFromPos: boolean;
  trialBanner: React.ReactNode;
  activeCashSessionId: string | null;
  cashSessionLoading: boolean;
  cashSessionReady: boolean;
  cashSessionOpenedAt: string | null;
  cashSessionOpeningAmount: number | null;
  handleOpenCashManagementFromCheckout: () => void;
  openSalesHistory: () => void;
  navigation: { navigate: (screen: string) => void };
  barcodeAllowCamera: boolean;
  allowPosBarcodeEnterSubmit: boolean;
  handlePosBarcodeHardwareSubmit: () => void;
  setScannerVisible: (visible: boolean) => void;
  setManualBarcodeVisible: (visible: boolean) => void;
  setVariosVisible: (visible: boolean) => void;
  setRecargaVisible?: (visible: boolean) => void;
  allowsExternalServices?: boolean;
  allowsMiscQuickSale?: boolean;
  lookupFeedback: string | null;
  setLookupFeedback: (value: string | null) => void;
  hasKitchenCapability?: boolean;
  /** Mesa vinculada: oculta Para llevar / Comer acá y fuerza dine_in. */
  diningTableId?: string | null;
  onProductPress: (product: ProductSearchRow) => void;
};

export function usePosCatalogPane({
  currentBranchId,
  windowWidth,
  selectedCategory,
  setCategory,
  categories,
  canEdit,
  canViewPos,
  canOpenCashManagementFromPos,
  trialBanner,
  activeCashSessionId,
  cashSessionLoading,
  cashSessionReady,
  cashSessionOpenedAt,
  cashSessionOpeningAmount,
  handleOpenCashManagementFromCheckout,
  openSalesHistory,
  navigation,
  barcodeAllowCamera,
  allowPosBarcodeEnterSubmit,
  handlePosBarcodeHardwareSubmit,
  setScannerVisible,
  setManualBarcodeVisible,
  setVariosVisible,
  setRecargaVisible,
  allowsExternalServices = false,
  allowsMiscQuickSale = true,
  lookupFeedback,
  setLookupFeedback,
  hasKitchenCapability = false,
  diningTableId = null,
  onProductPress,
}: Params) {
  const { layoutWide: isWidePosLayout } = useTabWorkspaceLayout();
  const [listViewportHeight, setListViewportHeight] = useState(0);
  const [listHeaderHeight, setListHeaderHeight] = useState(0);

  useEffect(() => {
    if (diningTableId) setPosFulfillmentType('dine_in');
  }, [diningTableId]);

  const filters = usePosCatalogFilters({
    currentBranchId,
    selectedCategory,
    setCategory,
    categories,
  });

  const { entitlements } = usePlanEntitlements();
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const rail = usePosQuickAccessRail({
    currentBranchId,
    selectedCategory,
    hasServicePackages: useHasBusinessCapability('service_packages'),
    promotionsEnabled:
      isGlobalFeatureOn(globalFeatureFlags, 'promotions_integrations') &&
      planAllowsPromotions(entitlements),
    setCategory,
    setItemTypeFilter: filters.handleSetItemTypeFilter,
    clearSearch: () => filters.setSearchQuery(''),
    reloadNonce: 0,
  });

  const deskCatalogListHeaderActive = useMemo(
    () =>
      posDeskCatalogListHeaderActive({
        showFulfillmentToggle: hasKitchenCapability && !diningTableId,
        layoutWide: isWidePosLayout,
        railCounts: rail.counts,
        categoryOptions: filters.categoryOptions,
        hasCategoryPicker: true,
      }),
    [
      diningTableId,
      filters.categoryOptions,
      hasKitchenCapability,
      isWidePosLayout,
      rail.counts,
    ],
  );

  const layout = usePosCatalogLayout({
    windowWidth,
    isWidePosLayout,
    listViewportHeight,
    listHeaderHeight,
  });

  const onCatalogListViewportHeight = useCallback((height: number) => {
    if (!(height > 0)) return;
    setListViewportHeight((prev) =>
      Math.abs(prev - height) < VIEWPORT_HEIGHT_EPSILON_PX ? prev : height
    );
  }, []);

  const onCatalogListHeaderHeight = useCallback((height: number) => {
    if (!(height > 0)) return;
    setListHeaderHeight((prev) =>
      Math.abs(prev - height) < VIEWPORT_HEIGHT_EPSILON_PX ? prev : height
    );
  }, []);

  useEffect(() => {
    if (isWidePosLayout && !deskCatalogListHeaderActive) {
      setListHeaderHeight(0);
    }
  }, [deskCatalogListHeaderActive, isWidePosLayout]);

  const {
    productListRef,
    products,
    catalogLoading,
    catalogCriteriaRefreshing,
    hasMore,
    catalogTotalCount,
    reloadCatalog,
    catalogPageIndex,
    catalogPageSize,
    setCatalogPageSize: _setCatalogPageSize,
    loadCatalogNextPage,
    loadCatalogPreviousPage,
  } = usePosCatalogPage({
    currentBranchId,
    selectedCategory,
    searchQuery: filters.effectiveSearchQuery,
    itemTypeFilter: filters.itemTypeFilter,
    posPhysicalStockPositive: Boolean(currentBranchId) && filters.posPhysicalStockOnly,
    brandId: filters.posBrandFilter,
    supplierId: filters.posSupplierFilter,
    posProductIds: rail.posProductIds,
    pageSizePreset: isWidePosLayout ? 'wide' : 'compact',
    pageSizeOptions: layout.catalogPageSizeConfig.options,
    pageSizeDefault: layout.catalogPageSizeConfig.defaultSize,
    pageSizeControlled: isWidePosLayout ? layout.viewportPageSize : null,
  });

  const posCatalogPaginationDock = (
    <CatalogDeskFooter
      pageIndex={catalogPageIndex}
      pageSize={catalogPageSize}
      productCount={products.length}
      totalCount={catalogTotalCount}
      hasMore={hasMore}
      loading={catalogLoading || catalogCriteriaRefreshing}
      onPreviousPage={loadCatalogPreviousPage}
      onNextPage={loadCatalogNextPage}
      embedded
      contentPaddingHorizontal={POS_DESK_CATALOG_GUTTER}
    />
  );

  const posCatalogMobilePagination = (
    <StockMobilePagination
      pageIndex={catalogPageIndex}
      pageSize={catalogPageSize}
      totalCount={catalogTotalCount ?? 0}
      productCount={products.length}
      loading={catalogLoading}
      hasMore={hasMore}
      onPreviousPage={loadCatalogPreviousPage}
      onNextPage={loadCatalogNextPage}
      variant="compact"
    />
  );

  const clearCatalogFiltersAndRail = useCallback(() => {
    filters.handleClearCatalogFilters();
    rail.selectRail('all');
  }, [filters, rail]);

  const openPosScannerOrManualCode = useCallback(() => {
    if (barcodeAllowCamera) {
      setScannerVisible(true);
      return;
    }
    setManualBarcodeVisible(true);
  }, [barcodeAllowCamera, setScannerVisible, setManualBarcodeVisible]);

  const posCatalogHeaderProps = useMemo(
    () => ({
      canEdit,
      showFulfillmentToggle: hasKitchenCapability && !diningTableId,
      fulfillmentLockedTo: diningTableId ? ('dine_in' as const) : null,
      trialBanner,
      activeCashSessionId,
      cashSessionLoading,
      cashSessionReady,
      cashSessionOpenedAt,
      cashSessionOpeningAmount,
      selectedCategory,
      itemTypeFilter: filters.itemTypeFilter,
      categories: filters.categoryOptions,
      searchQuery: filters.searchQuery,
      lookupFeedback,
      onSetCategory: setCategory,
      onSetItemTypeFilter: filters.handleSetItemTypeFilter,
      onSetSearchQuery: filters.setSearchQuery,
      onOpenCategorySelector: () => filters.setCategorySelectorVisible(true),
      onOpenScanner: openPosScannerOrManualCode,
      onOpenVarious: () => setVariosVisible(true),
      onOpenRecarga:
        allowsExternalServices && setRecargaVisible ? () => setRecargaVisible(true) : undefined,
      allowsMiscQuickSale,
      showBarcodeCameraButton: barcodeAllowCamera,
      onBarcodeSearchSubmit: allowPosBarcodeEnterSubmit && canEdit ? handlePosBarcodeHardwareSubmit : undefined,
      autoFocusSearchOnWeb: false,
      onOpenCashManagement:
        canOpenCashManagementFromPos && cashSessionReady
          ? handleOpenCashManagementFromCheckout
          : undefined,
      onOpenSalesHistory: canViewPos ? openSalesHistory : undefined,
      onOpenPromotions: canViewPos ? () => navigation.navigate('PromotionsList') : undefined,
      posPhysicalStockOnly: filters.posPhysicalStockOnly,
      stockOnlyFilterDisabled: !currentBranchId,
      onTogglePosPhysicalStockOnly: filters.togglePosPhysicalStockOnly,
      posBrandFilter: filters.posBrandFilter,
      onPosBrandFilterChange: filters.setPosBrandFilter,
      posBrandOptions: filters.posBrandOptions,
      onPosSupplierFilterChange: filters.setPosSupplierFilter,
      posSupplierOptions: filters.posSupplierOptions,
      posSupplierFilter: filters.posSupplierFilter,
      hasActiveCatalogFilters: filters.hasPosCatalogCriteria,
      onClearCatalogFilters: clearCatalogFiltersAndRail,
      rail: {
        selection: rail.railView,
        onSelect: (sel: string) => rail.selectRail(parseRailSelection(sel)),
        categories: filters.categoryOptions,
        counts: rail.counts,
        categoryPickerLabel:
          selectedCategory === 'all'
            ? 'Todas'
            : (filters.categoryOptions.find((c) => String(c.id) === String(selectedCategory))?.label ??
              'Elegí'),
        onOpenCategorySelector: () => filters.setCategorySelectorVisible(true),
      },
    }),
    [
      canEdit,
      hasKitchenCapability,
      diningTableId,
      trialBanner,
      activeCashSessionId,
      cashSessionLoading,
      cashSessionReady,
      cashSessionOpenedAt,
      cashSessionOpeningAmount,
      selectedCategory,
      filters,
      lookupFeedback,
      setCategory,
      barcodeAllowCamera,
      allowPosBarcodeEnterSubmit,
      handlePosBarcodeHardwareSubmit,
      canOpenCashManagementFromPos,
      handleOpenCashManagementFromCheckout,
      canViewPos,
      openSalesHistory,
      navigation,
      currentBranchId,
      rail,
      openPosScannerOrManualCode,
      setVariosVisible,
      setRecargaVisible,
      allowsExternalServices,
      allowsMiscQuickSale,
      clearCatalogFiltersAndRail,
    ]
  );

  const packageMode = filters.itemTypeFilter === 'package';

  const posCatalogEmptyProps = useMemo(() => {
    const categoryLabel =
      selectedCategory === 'all'
        ? 'Todas'
        : (filters.categoryOptions.find((c) => String(c.id) === String(selectedCategory))?.label ??
          'Categoría');

    const panelFiltersActive = hasPosCatalogPanelFilters({
      selectedCategory,
      categoryLabel,
      itemTypeFilter: filters.itemTypeFilter,
      posPhysicalStockOnly: Boolean(currentBranchId) && filters.posPhysicalStockOnly,
      posBrandFilter: filters.posBrandFilter,
      posBrandName: filters.posBrandOptions.find((b) => b.id === filters.posBrandFilter)?.name ?? null,
      posSupplierFilter: filters.posSupplierFilter,
      posSupplierName:
        filters.posSupplierOptions.find((s) => s.id === filters.posSupplierFilter)?.name ?? null,
    });

    const criteriaActive = filters.hasPosCatalogCriteria;
    const clearAction = {
      label: panelFiltersActive ? 'Limpiar filtros' : 'Limpiar búsqueda',
      onPress: clearCatalogFiltersAndRail,
      testID: 'pos.catalog.empty.clear-filters',
    };

    if (packageMode) {
      return {
        loading: catalogLoading && products.length === 0,
        loadingText: 'Cargando paquetes…',
        title: criteriaActive
          ? panelFiltersActive
            ? 'Nada coincide'
            : 'No encontramos paquetes'
          : 'Todavía no hay paquetes',
        subtitle: criteriaActive
          ? panelFiltersActive
            ? 'Sacá un filtro o limpiá todo.'
            : 'Probá otra búsqueda.'
          : 'Los paquetes se crean desde Agenda. Cada uno se vende como un ítem más.',
        secondaryAction: criteriaActive ? clearAction : undefined,
        primaryAction: canEdit
          ? { label: 'Ir a Agenda', onPress: () => navigation.navigate('Appointments') }
          : undefined,
        testID: 'pos.catalog.empty',
      };
    }

    return {
      loading: catalogLoading && products.length === 0,
      loadingText: 'Cargando catálogo…',
      title: criteriaActive
        ? panelFiltersActive
          ? 'Nada coincide'
          : 'No encontramos ítems'
        : 'Todavía no hay productos',
      subtitle: criteriaActive
        ? panelFiltersActive
          ? 'Sacá un filtro o limpiá todo.'
          : 'Probá otra búsqueda o el código completo.'
        : 'Cargá lo que vendés para poder cobrar. Empezá desde Catálogo.',
      secondaryAction: criteriaActive ? clearAction : undefined,
      primaryAction:
        canEdit && !criteriaActive
          ? { label: 'Ir a Catálogo', onPress: () => navigation.navigate('Catalog') }
          : undefined,
      testID: 'pos.catalog.empty',
    };
  }, [
    packageMode,
    catalogLoading,
    products.length,
    selectedCategory,
    filters.categoryOptions,
    filters.itemTypeFilter,
    filters.posPhysicalStockOnly,
    filters.posBrandFilter,
    filters.posBrandOptions,
    filters.posSupplierFilter,
    filters.posSupplierOptions,
    filters.hasPosCatalogCriteria,
    currentBranchId,
    clearCatalogFiltersAndRail,
    canEdit,
    navigation,
  ]);

  return {
    productListRef,
    products,
    catalogLoading,
    reloadCatalog,
    searchQuery: filters.searchQuery,
    setSearchQuery: filters.setSearchQuery,
    lookupFeedback,
    setLookupFeedback,
    itemTypeFilter: filters.itemTypeFilter,
    handleSetItemTypeFilter: filters.handleSetItemTypeFilter,
    categoryOptions: filters.categoryOptions,
    categorySelectorVisible: filters.categorySelectorVisible,
    setCategorySelectorVisible: filters.setCategorySelectorVisible,
    numColumns: layout.numColumns,
    catalogPaneStyle: layout.catalogPaneStyle,
    posCatalogListContentStyle: layout.posCatalogListContentStyle(
      products.length,
      catalogLoading,
      deskCatalogListHeaderActive,
    ),
    deskCatalogListHeaderActive,
    posCatalogHeaderProps,
    openPosScannerOrManualCode,
    posCatalogEmptyProps,
    posCatalogPaginationDock,
    posCatalogMobilePagination,
    toggleProductFavorite: rail.toggleFavorite,
    isProductFavorite: rail.isFavorite,
    isProductCombo: rail.isCombo,
    productHasPromo: rail.hasPromo,
    railView: rail.railView,
    onCatalogListViewportHeight,
    onCatalogListHeaderHeight,
    listHeaderHeight,
    handleSelectCategoryFromMore: filters.handleSelectCategoryFromMore,
    reloadFilterOptions: filters.reloadFilterOptions,
  };
}
